import { useTranslation } from 'react-i18next'
import { Box, Button } from '@mui/material'
import { FilterList as FilterListIcon } from '@mui/icons-material'

interface FiltersButtonProps {
  /** Раскрыта ли панель фильтров: определяет вариант кнопки (contained/outlined) */
  open: boolean
  /** Готовое число применённых фильтров — компонент не знает, что именно считает страница */
  count: number
  onToggle: () => void
  /** Подпись кнопки; по умолчанию общий ключ common.filters */
  label?: string
}

/**
 * Кнопка «Фильтры» со счётчиком применённых фильтров.
 *
 * Вынесена в общий компонент, потому что одинаковая разметка кнопки и бейджа
 * уже дублировалась на страницах домашек и уроков: любая правка внешнего вида
 * требовала синхронных изменений в двух местах и расходилась по мелочам
 * (например, aria-label собирался то через t(), то шаблонной строкой).
 */
export function FiltersButton({ open, count, onToggle, label }: FiltersButtonProps) {
  const { t } = useTranslation()
  const hasActiveFilters = count > 0

  return (
    // relative-обёртка нужна, чтобы бейдж позиционировался от кнопки, а не от строки фильтров
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Button
        size="small"
        variant={open ? 'contained' : 'outlined'}
        startIcon={<FilterListIcon />}
        onClick={onToggle}
        sx={{ height: 40 }}
      >
        {label ?? t('common.filters', 'Фильтры')}
      </Button>
      {hasActiveFilters && (
        <Box
          aria-label={t('common.filtersActive', 'Активные фильтры: {{count}}', { count })}
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            height: 20,
            minWidth: 20,
            px: 0.75,
            borderRadius: '999px',
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            fontSize: '0.7rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: (theme) => theme.shadows[2],
            // клики по бейджу должны доходить до кнопки под ним
            pointerEvents: 'none',
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  )
}
